import { useEffect, useId, useRef, type ReactNode } from 'react';

/** A labelled control. The label is tied to the first control by id. */
export function Field({ label, hint, error, children, id }: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  id: string;
  children: ReactNode;
}) {
  return (
    <div className="el-field" data-invalid={error ? 'true' : undefined}>
      <label htmlFor={id} className="el-label">{label}</label>
      {children}
      {error ? <span className="el-field-error" role="alert">{error}</span> : hint ? <span className="el-hint">{hint}</span> : null}
    </div>
  );
}

export function Tabs<K extends string>({ value, onChange, tabs, label }: {
  value: K;
  onChange: (k: K) => void;
  tabs: { key: K; label: ReactNode; count?: number }[];
  label: string;
}) {
  return (
    <div className="el-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" className="el-tab" aria-selected={value === t.key} onClick={() => onChange(t.key)}>
          {t.label}
          {t.count !== undefined && <span className="el-tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * A modal built into the page (browsers' confirm() isn't used anywhere).
 * Escape or a click on the shade closes it; focus moves to the first field.
 */
export function Dialog({ title, onClose, actions, children, wide }: {
  title: string;
  onClose: () => void;
  actions: ReactNode;
  children: ReactNode;
  /** Wider (up to 760px) for forms with more on a line, such as a customer's contact people. */
  wide?: boolean;
}) {
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const first = box.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="el-shade" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className="el-dialog" data-wide={wide || undefined} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="el-h2">{title}</h2>
        {children}
        <div className="el-dialog-actions">{actions}</div>
      </div>
    </div>
  );
}

export function Toast({ message }: { message: string | null }) {
  return message ? <div className="el-toast" role="status">{message}</div> : null;
}
